import { Temporal } from 'temporal-polyfill';

import { DateInputError, formatIsoUtc, formatLocalIso, formatSpreadsheet, formatUnixMilliseconds, formatUnixSeconds, validateTimeZone } from './date-engine.js';
import { formatCustom } from './custom-format.js';
import { formatSourceInstant, parseDateInput } from './input-parser.js';

const LITERAL_PLUS_SENTINEL = '\u{F0000}';
const EXPLICIT_PREFIX = /^(?:unix|unix-ms|spreadsheet|iso|custom)\//i;
const ISO_WITH_OFFSET = /^(?:\d{4}|[+-]\d{6})-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i;
const SOURCE_PREFIX = Object.freeze({
  'unix-seconds': 'unix',
  unix: 'unix',
  'unix-milliseconds': 'unix-ms',
  'unix-ms': 'unix-ms',
  spreadsheet: 'spreadsheet',
  iso: 'iso',
  local: 'iso',
  custom: 'custom',
});
const MAX_PATH_LENGTH = 4_096;

function routeError(message, code = 'INVALID_DATE_URL', details = {}) {
  return new DateInputError(message, code, details);
}

function encodedPathBody(pathname) {
  if (typeof pathname !== 'string') {
    throw routeError('A URL pathname is required.', 'INVALID_DATE_URL', { pathname });
  }
  if (pathname.length > MAX_PATH_LENGTH) {
    throw routeError(`Date URLs are limited to ${MAX_PATH_LENGTH} characters.`, 'DATE_URL_TOO_LONG');
  }
  return pathname.replace(/^\/+/, '').replace(/\/+$/, '');
}

function decodeComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch (cause) {
    throw routeError('The date URL contains malformed percent encoding.', 'MALFORMED_DATE_URL', {
      value,
      cause: cause?.message,
    });
  }
}

/** Decode a path literally; unlike form/query decoding, `+` remains a plus. */
export function decodeLiteralPath(pathname) {
  return decodeComponent(encodedPathBody(pathname));
}

/**
 * Decode the natural-language path convention. Literal path plus signs become
 * spaces, while percent-encoded `%2B` remains the arithmetic operator.
 */
export function decodeNaturalPath(pathname) {
  const encoded = encodedPathBody(pathname).replace(/%2B/gi, LITERAL_PLUS_SENTINEL);
  return decodeComponent(encoded).replace(/\+/g, ' ').replaceAll(LITERAL_PLUS_SENTINEL, '+');
}

/** Encode spaces readably as `+`, while real plus operators become `%2B`. */
export function encodeNaturalExpression(expression) {
  if (typeof expression !== 'string' || expression.trim() === '') {
    throw routeError('A natural-language expression is required.', 'EMPTY_DATE_INPUT');
  }
  return encodeURIComponent(expression.trim()).replace(/%20/g, '+');
}

function containsLiteralPlus(encoded) {
  return encoded.replace(/%[0-9a-f]{2}/gi, '').includes('+');
}

function normalizeParserOptions(rawOptions = {}) {
  return {
    ...rawOptions,
    zone: rawOptions.zone ?? rawOptions.timeZone ?? rawOptions.defaultZone ?? 'UTC',
    referenceInstant: rawOptions.referenceInstant ?? rawOptions.now,
    customDialect: rawOptions.customDialect ?? rawOptions.dialect,
    customPattern: rawOptions.customPattern ?? rawOptions.pattern ?? rawOptions.fmt,
  };
}

/** Parse one non-root pathname without allowing `+` decoding to damage ISO offsets. */
export function parseDatePath(pathname, rawOptions = {}) {
  const encoded = encodedPathBody(pathname);
  if (!encoded) {
    throw routeError('The root URL is live mode and has no fixed date to parse.', 'LIVE_ROUTE');
  }

  const options = normalizeParserOptions(rawOptions);
  const literal = decodeComponent(encoded);
  const literalSensitive = EXPLICIT_PREFIX.test(literal) || ISO_WITH_OFFSET.test(literal);
  if (literalSensitive || !containsLiteralPlus(encoded)) {
    return parseDateInput(literal, options);
  }

  const natural = decodeNaturalPath(pathname);
  try {
    return parseDateInput(natural, options);
  } catch (naturalError) {
    try {
      return parseDateInput(literal, options);
    } catch {
      throw naturalError;
    }
  }
}

function urlFromLocationLike(locationLike) {
  if (locationLike instanceof URL) return new URL(locationLike.href);
  if (typeof locationLike === 'string') {
    return new URL(locationLike, 'https://time.brent.app');
  }
  if (locationLike?.href) return new URL(locationLike.href, 'https://time.brent.app');
  if (locationLike && typeof locationLike.pathname === 'string') {
    return new URL(
      `${locationLike.pathname}${locationLike.search ?? ''}${locationLike.hash ?? ''}`,
      locationLike.origin ?? 'https://time.brent.app',
    );
  }
  return new URL('https://time.brent.app/');
}

function queryChoice(searchParams, key, allowed, fallback) {
  const value = searchParams.get(key);
  if (value === null || value === '') return fallback;
  if (!allowed.includes(value)) {
    throw routeError(`Unsupported ${key} URL option: ${value}`, 'INVALID_URL_METADATA', {
      key,
      value,
      allowed,
    });
  }
  return value;
}

/**
 * Parse a browser Location, URL, absolute URL, or relative path into app state.
 */
export function parseUrlState(locationLike, rawOptions = {}) {
  const url = urlFromLocationLike(locationLike);
  const params = url.searchParams;
  const zone = validateTimeZone(params.get('tz') ?? rawOptions.defaultZone ?? rawOptions.zone ?? 'UTC');
  const dateOrder = queryChoice(
    params,
    'order',
    ['MDY', 'DMY'],
    rawOptions.dateOrder === 'DMY' ? 'DMY' : 'MDY',
  );
  const fold = queryChoice(params, 'fold', ['earlier', 'later', 'reject'], rawOptions.fold ?? 'reject');
  const dialect = params.get('dialect') ?? rawOptions.customDialect ?? rawOptions.dialect ?? undefined;
  const pattern = params.get('fmt') ?? rawOptions.customPattern ?? rawOptions.pattern ?? undefined;
  const referenceInstant = rawOptions.referenceInstant ?? rawOptions.now ?? Temporal.Now.instant();

  if (!encodedPathBody(url.pathname)) {
    const liveInstant = parseDateInput('now', { zone, referenceInstant, fold: 'earlier' }).instant;
    return Object.freeze({
      mode: 'live',
      instant: liveInstant,
      source: null,
      provenance: null,
      zone,
      timeZone: zone,
      dateOrder,
      fold: 'none',
      url,
      metadata: Object.freeze({ zone, dateOrder, fold: 'none', dialect, pattern }),
    });
  }

  const parsed = parseDatePath(url.pathname, {
    zone,
    dateOrder,
    dateOrderConfirmed: params.has('order'),
    referenceInstant,
    fold,
    customDialect: dialect,
    customPattern: pattern,
  });
  return Object.freeze({
    mode: 'fixed',
    ...parsed,
    zone,
    timeZone: zone,
    dateOrder,
    url,
    metadata: Object.freeze({ zone, dateOrder, fold: parsed.fold, dialect, pattern }),
  });
}

function encodeReadablePathValue(value, { preserveSlash = false, preservePlus = false } = {}) {
  let encoded = encodeURIComponent(value)
    .replace(/%3A/gi, ':')
    .replace(/%2C/gi, ',');
  if (preserveSlash) encoded = encoded.replace(/%2F/gi, '/');
  if (preservePlus) encoded = encoded.replace(/%2B/gi, '+');
  return encoded;
}

function provenanceFrom(input) {
  return input?.source ?? input?.provenance ?? input;
}

/** Build a pathname for a parsed result/source while retaining its provenance. */
export function buildDatePath(input, rawOptions = {}) {
  const provenance = provenanceFrom(input);
  const instant = rawOptions.instant ?? input?.instant;
  if (!provenance?.kind || !instant) {
    throw routeError('Building a date path requires an instant and source provenance.', 'MISSING_LINK_SOURCE');
  }
  const zone = rawOptions.zone ?? rawOptions.timeZone ?? provenance.timeZone ?? 'UTC';
  const raw = provenance.kind === 'natural'
    ? provenance.raw
    : formatSourceInstant(provenance, instant, zone);

  if (provenance.kind === 'natural') return `/${encodeNaturalExpression(raw)}`;

  const explicit = rawOptions.explicit ?? rawOptions.forceExplicit ?? provenance.explicit ?? false;
  const prefix = SOURCE_PREFIX[provenance.kind];
  const encoded = encodeReadablePathValue(raw, {
    preserveSlash: provenance.kind === 'custom',
    preservePlus: provenance.kind === 'iso' || provenance.kind === 'local',
  });
  if (explicit && prefix) return `/${prefix}/${encoded}`;
  return `/${encoded}`;
}

function needsZone(provenance) {
  return ['spreadsheet', 'custom', 'local', 'natural'].includes(provenance?.kind);
}

function linkSearchParams(provenance, options) {
  const params = new URLSearchParams();
  const zone = options.zone ?? options.timeZone ?? provenance.timeZone;
  if ((options.includeZone || needsZone(provenance)) && zone) params.set('tz', zone);

  if (provenance.kind === 'custom') {
    const dialect = options.dialect ?? provenance.dialect;
    const pattern = options.pattern ?? options.fmt ?? provenance.pattern ?? provenance.fmt;
    if (dialect) params.set('dialect', dialect);
    if (pattern) params.set('fmt', pattern);
  }

  const dateOrder = options.dateOrder ?? provenance.dateOrder;
  if (dateOrder && (options.includeDateOrder || provenance.candidate?.ambiguous)) {
    params.set('order', dateOrder);
  }

  const fold = options.fold ?? provenance.fold;
  if (
    (fold === 'earlier' || fold === 'later') &&
    (options.includeFold || ['spreadsheet', 'custom', 'natural'].includes(provenance.kind))
  ) {
    params.set('fold', fold);
  }
  return params;
}

/** Build a relative or absolute URL from a result/source. */
export function buildShareUrl(input, rawOptions = {}) {
  const provenance = provenanceFrom(input);
  const path = buildDatePath(input, rawOptions);
  const params = linkSearchParams(provenance, rawOptions);
  const suffix = params.size ? `?${params.toString()}` : '';
  if (!rawOptions.origin) return `${path}${suffix}`;
  return `${String(rawOptions.origin).replace(/\/$/, '')}${path}${suffix}`;
}

function normalizedRowKind(rowKind) {
  const key = String(rowKind ?? '').trim().toLocaleLowerCase('en-US').replace(/[ _]+/g, '-');
  const aliases = {
    unix: 'unix-seconds',
    'unix-seconds': 'unix-seconds',
    'unix-second': 'unix-seconds',
    'unix-ms': 'unix-milliseconds',
    'unix-milliseconds': 'unix-milliseconds',
    spreadsheet: 'spreadsheet',
    sheet: 'spreadsheet',
    iso: 'iso',
    'iso-8601': 'iso',
    local: 'local',
    custom: 'custom',
  };
  return aliases[key];
}

/** Build the absolute Copy-Link target for one editable conversion row. */
export function buildRowLink({
  origin = 'https://time.brent.app',
  instant,
  rowKind,
  zone = 'UTC',
  dateOrder,
  fold,
  dialect,
  pattern,
}) {
  const kind = normalizedRowKind(rowKind);
  if (!kind) {
    throw routeError(`Unknown date row: ${String(rowKind)}`, 'UNKNOWN_SOURCE_FORMAT', { rowKind });
  }
  const timeZone = validateTimeZone(zone);
  let raw;
  switch (kind) {
    case 'unix-seconds': raw = formatUnixSeconds(instant); break;
    case 'unix-milliseconds': raw = formatUnixMilliseconds(instant); break;
    case 'spreadsheet': raw = formatSpreadsheet(instant, timeZone); break;
    case 'iso': raw = formatIsoUtc(instant); break;
    case 'local': raw = formatLocalIso(instant, timeZone); break;
    case 'custom':
      if (!dialect || !pattern) {
        throw routeError('The Custom row needs a dialect and pattern before it can be linked.', 'CUSTOM_METADATA_REQUIRED');
      }
      raw = formatCustom(instant, timeZone, dialect, pattern);
      break;
    default: throw routeError('Unsupported row format.', 'UNKNOWN_SOURCE_FORMAT', { rowKind });
  }

  const provenance = {
    kind,
    type: kind,
    raw,
    explicit: true,
    timeZone,
    fold,
    dateOrder,
    ...(kind === 'custom' ? { dialect, pattern, fmt: pattern } : {}),
  };
  return buildShareUrl(
    { instant, source: provenance },
    {
      origin,
      zone: timeZone,
      dateOrder,
      fold,
      dialect,
      pattern,
      explicit: true,
    },
  );
}

export const parseLocationState = parseUrlState;
export const encodeNaturalPath = encodeNaturalExpression;
